<template>
	<MDBContainer class="h-100">
		<MDBRow class="h-100">
			<section class="w-100">
				<base-title>Profile Settings</base-title>
				<div
					v-if="isLoading"
					class="d-flex align-items-center justify-content-center h-25"
				>
					<base-spinner></base-spinner>
				</div>
				<div v-else class="w-100 d-flex justify-content-center py-5">
					<div class="profile-form w-50">
						<!-- Token Section -->
						<MDBCard class="mb-4">
							<MDBCardBody>
								<h5 class="card-title mb-4">
									<i class="fas fa-key me-2"></i>
									Monobank API Token
								</h5>
								<div class="mb-3">
									<div class="input-group">
										<MDBInput
											label="API Token"
											:type="showToken ? 'text' : 'password'"
											v-model.trim="token"
											:class="{ 'is-valid': tokenSaved }"
										/>
										<button
											class="btn btn-outline-secondary"
											type="button"
											@click="showToken = !showToken"
											title="Toggle token visibility"
										>
											<i :class="showToken ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
										</button>
									</div>
									<small class="form-text text-muted d-block mt-2">
										<i class="fas fa-info-circle"></i>
										Токен зберігається зашифрованим у базі даних
									</small>
								</div>
								<div v-if="lastUpdated" class="mb-3">
									<small class="text-muted">
										<i class="fas fa-clock me-1"></i>
										Останнє оновлення: {{ formatDate(lastUpdated) }}
									</small>
								</div>
								<div class="d-flex gap-2">
									<MDBBtn
										color="primary"
										@click="saveToken"
										:disabled="!token || isSaving"
									>
										<i class="fas fa-save me-2"></i>
										{{ isSaving ? 'Збереження...' : 'Зберегти токен' }}
									</MDBBtn>
									<MDBBtn
										color="secondary"
										outline
										@click="loadToken"
										:disabled="isLoadingToken"
									>
										<i class="fas fa-sync-alt me-2"></i>
										{{ isLoadingToken ? 'Завантаження...' : 'Оновити' }}
									</MDBBtn>
								</div>
								<div class="mt-3">
									<small class="text-muted">
										<i class="fas fa-external-link-alt me-1"></i>
										Отримати токен можна на
										<a
											href="https://api.monobank.ua/"
											target="_blank"
											rel="noopener noreferrer"
										>
											https://api.monobank.ua/
										</a>
									</small>
								</div>
							</MDBCardBody>
						</MDBCard>

						<!-- Success Message -->
						<div v-if="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
							<i class="fas fa-check-circle me-2"></i>
							{{ successMessage }}
							<button type="button" class="btn-close" @click="successMessage = null" title="Close"></button>
						</div>

						<!-- Error Message -->
						<div v-if="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
							<i class="fas fa-exclamation-circle me-2"></i>
							{{ errorMessage }}
							<button type="button" class="btn-close" @click="errorMessage = null" title="Close"></button>
						</div>
					</div>
				</div>
			</section>
		</MDBRow>
	</MDBContainer>
</template>

<script>
import {
	MDBInput,
	MDBBtn,
	MDBContainer,
	MDBRow,
	MDBCard,
	MDBCardBody,
} from "mdb-vue-ui-kit";

export default {
	components: {
		MDBInput,
		MDBBtn,
		MDBContainer,
		MDBRow,
		MDBCard,
		MDBCardBody,
	},
	data() {
		return {
			token: '',
			showToken: false,
			lastUpdated: null,
			isLoading: false,
			isSaving: false,
			isLoadingToken: false,
			tokenSaved: false,
			successMessage: null,
			errorMessage: null,
		};
	},
	async mounted() {
		await this.loadToken();
	},
	methods: {
		async loadToken() {
			this.isLoadingToken = true;
			this.errorMessage = null;

			try {
				const savedToken = await this.$store.dispatch('getToken');
				if (savedToken) {
					this.token = savedToken;
					this.tokenSaved = true;

					// Завантажуємо дату оновлення
					const userId = this.$store.getters.userId;
					const tokenData = await this.$store.dispatch('getTokenMetadata', userId);
					if (tokenData && tokenData.updatedAt) {
						this.lastUpdated = tokenData.updatedAt;
					}
				} else {
					this.token = '';
					this.tokenSaved = false;
					this.lastUpdated = null;
				}
			} catch (error) {
				console.error('Помилка завантаження токена:', error);
				this.errorMessage = 'Не вдалося завантажити токен';
			} finally {
				this.isLoadingToken = false;
			}
		},

		async saveToken() {
			if (!this.token) {
				this.errorMessage = 'Будь ласка, введіть токен';
				return;
			}

			this.isSaving = true;
			this.errorMessage = null;
			this.successMessage = null;

			try {
				const userId = this.$store.getters.userId;
				await this.$store.dispatch('saveToken', {
					token: this.token,
					userId: userId
				});

				this.tokenSaved = true;
				this.lastUpdated = Date.now();
				this.successMessage = 'Токен успішно збережено!';

				setTimeout(() => {
					this.successMessage = null;
				}, 3000);
			} catch (error) {
				console.error('Помилка збереження токена:', error);
				this.errorMessage = 'Не вдалося зберегти токен. Спробуйте ще раз.';
			} finally {
				this.isSaving = false;
			}
		},

		formatDate(timestamp) {
			const date = new Date(timestamp);
			return date.toLocaleString('uk-UA', {
				year: 'numeric',
				month: 'long',
				day: 'numeric',
				hour: '2-digit',
				minute: '2-digit'
			});
		}
	}
};
</script>

<style scoped lang="scss">
.profile-form {
	max-width: 600px;
}

@media (max-width: 768px) {
	.profile-form {
		width: 90% !important;
	}
}

.input-group {
	display: flex;
}

.input-group .btn {
	border-top-left-radius: 0;
	border-bottom-left-radius: 0;
}

.gap-2 {
	gap: 0.5rem;
}

.card-title {
	color: #333;
	font-weight: 600;
}

.alert {
	animation: slideIn 0.3s ease-in-out;
}

@keyframes slideIn {
	from {
		opacity: 0;
		transform: translateY(-10px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}
</style>
